import { AxiosResponse } from 'axios';
import 'dayjs/locale/ko';

import { useQueryClient } from '@tanstack/react-query';

import { TypeCodeState } from '../../types';
import { axiosInstance } from '~/services/fetchServiceV2';
import { TypeErrorFormat, requestHelper } from '~/utils/fetchHelper';

type TypeKurlyWorksWorkShiftParams = {
  cluster: string;
  center: string;
  workPart: string;
  teamName: string;
};

type TypeKurlyWorksWorkShiftResponse = {
  code: string;
  message: string;
  data: TypeKurlyWorksWorkShift[];
};

type TypeKurlyWorksWorkShift = {
  /** 근로계약 양식명 */
  name: string;
  /** 근로계약 양식 일련번호 */
  masterContractSeq: number;
  /** 근로계약 양식 변경 일련번호 */
  masterContractVersionSeq: number;
  /** 계약 가능 여부 */
  availableContract: boolean;
  /** 근무 시간대 */
  workTimeShift: string;
  /** 팀명 */
  teamName: string;
  /** 계약가능 시작시간 */
  availableStartTime: string;
  /** 계약가능 종료시간 */
  availableEndTime: string;
};

export type TypeKurlyWorksWorkShiftFormattedResponse = {
  optionList: TypeCodeState[];
  contractSeq: {
    [key: string]: {
      masterContractSeq: number;
      masterContractVersionSeq: number;
    };
  };
  availableContractTime: {
    [key: string]: {
      availableContractStartTime: string;
      availableContractEndTime: string;
    };
  };
};

export const handleSuccess = (response: TypeKurlyWorksWorkShiftResponse) => {
  if (!response) {
    return null;
  }

  const responseData = response?.data;

  const { optionList, contractSeq, availableContractTime } =
    responseData?.reduce(
      (acc, curr) => {
        const {
          name,
          workTimeShift,
          masterContractSeq,
          masterContractVersionSeq,
          availableContract,
          availableStartTime,
          availableEndTime,
        } = curr;

        if (!availableContract) {
          /** 사용 불가한 근무시간 제외 */
          return acc;
        }

        acc = {
          optionList: [...acc.optionList, { code: workTimeShift, text: name }],
          contractSeq: {
            ...acc.contractSeq,
            [workTimeShift]: {
              masterContractSeq,
              masterContractVersionSeq,
            },
          },
          availableContractTime: {
            ...acc.availableContractTime,
            [workTimeShift]: {
              availableContractStartTime: availableStartTime,
              availableContractEndTime: availableEndTime,
            },
          },
        };
        return acc;
      },
      {
        optionList: [] as TypeCodeState[],
        contractSeq: {} as {
          [key: string]: {
            masterContractSeq: number;
            masterContractVersionSeq: number;
          };
        },
        availableContractTime: {} as {
          [key: string]: {
            availableContractStartTime: string;
            availableContractEndTime: string;
          };
        },
      }
    ) || {};

  return { optionList, contractSeq, availableContractTime };
};

/** @description 근로계약 양식 조회 훅
 * @remarks
 * 1. 클러스터, 센터, 업무파트, 팀명을 전달하면 해당하는 근로계약 양식을 조회
 * 2. 근무 정보 등록 시, 양식 일련변호와 계약 가능 시간을 전달해야 함
 */
export const useGetKurlyWorksWorkShift = () => {
  const queryClient = useQueryClient();

  const requestGetKurlyWorksWorkShift = async ({
    params,
    onSuccess,
    onError,
  }: {
    params?: TypeKurlyWorksWorkShiftParams;
    onSuccess?: (response: TypeKurlyWorksWorkShiftFormattedResponse | null) => void;
    onError?: (error: TypeErrorFormat) => void;
  } = {}) => {
    const endpoint = 'master-contracts';
    const query = queryClient.ensureQueryData<AxiosResponse<TypeKurlyWorksWorkShiftResponse>>({
      queryKey: ['kurly-works-work-shift', params],
      queryFn: () => axiosInstance.get<TypeKurlyWorksWorkShiftResponse>(endpoint, { params }),
    });

    return requestHelper(
      () => query,
      (response) => onSuccess?.(handleSuccess(response)),
      onError
    );
  };

  return requestGetKurlyWorksWorkShift;
};
